import { useEffect, useMemo, useRef, useState } from "react";
import { fetchMeta, tileUrl, DEFAULT_META, type MapMeta } from "./mapApi";

/**
 * MapPreview — vignette statique de la NOUVELLE carte 3D pour l'Accueil.
 *
 * Léger (aucun moteur Three.js, aucun contrôle) : compose une petite grille de tuiles couleur
 * réelles autour du spawn via `/carte-live/api/tile` (proxy BFF, origine unique). Chaque tuile
 * dégrade indépendamment si elle manque ; si AUCUNE tuile ne charge, un fond stylisé « atlas »
 * reste visible. Ne throw jamais : la carte peut être momentanément indisponible.
 *
 * Rendu décoratif et non interactif — le clic est porté par le parent (lien vers /carte).
 */

/** Côté de la grille de tuiles (impair pour centrer sur le spawn). */
const GRID = 3;

/** Résolution d'une tuile en pixels (contrat carte : 256×256). */
const TILE_RASTER = 256;

/** Blocs couverts par une tuile à un zoom donné (miroir de TerrainStreamer, sans importer le moteur). */
function blocksPerTile(z: number): number {
  return TILE_RASTER / z;
}

interface PreviewTile {
  key: string;
  tx: number;
  ty: number;
  url: string;
}

/**
 * Construit la grille de tuiles couvrant le centre du monde au zoom le plus grossier
 * disponible. `bust` (anti-cache) force des tuiles fraîches lors des rafraîchissements.
 */
function buildTiles(meta: MapMeta, bust: string): PreviewTile[] {
  // DÉFENSIF : la méta peut arriver incomplète (proxy indisponible, forme réduite) — chaque
  // champ retombe sur DEFAULT_META. Une vignette décorative ne doit JAMAIS crasher l'Accueil.
  const levels = Array.isArray(meta.zoomLevels) && meta.zoomLevels.length > 0
      ? meta.zoomLevels
      : DEFAULT_META.zoomLevels;
  // Zoom le plus grossier = plus grande couverture par tuile → vue d'ensemble en peu de tuiles.
  const z = Math.min(...levels);
  const span = blocksPerTile(z);
  const cx = Math.floor((meta.borderCenterX ?? 0) / span);
  const cz = Math.floor((meta.borderCenterZ ?? 0) / span);
  const half = Math.floor(GRID / 2);
  const tiles: PreviewTile[] = [];
  for (let dz = -half; dz <= half; dz++) {
    for (let dx = -half; dx <= half; dx++) {
      const tx = cx + dx;
      const ty = cz + dz;
      tiles.push({
        key: `${z}/${tx}/${ty}`,
        tx,
        ty,
        url: tileUrl(meta.world ?? DEFAULT_META.world, z, tx, ty, bust || undefined),
      });
    }
  }
  return tiles;
}

export function MapPreview() {
  const [meta, setMeta] = useState<MapMeta | null>(null);
  const [failed, setFailed] = useState<Set<string>>(new Set());
  const [loadedCount, setLoadedCount] = useState(0);
  /** Jeton anti-cache : sa mise à jour force le rechargement des tuiles (temps réel). */
  const [bust, setBust] = useState("");
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    // Méta best-effort : en cas d'échec on retombe sur DEFAULT_META (spawn = 0,0) sans erreur.
    fetchMeta()
      .then((m) => {
        if (mounted.current) setMeta(m);
      })
      .catch(() => {
        if (mounted.current) setMeta(DEFAULT_META);
      });
    return () => {
      mounted.current = false;
    };
  }, []);

  // Rafraîchissement périodique (60 s) + au refocus de l'onglet : nouvelles tuiles
  // anti-cache, et on redonne leur chance aux tuiles précédemment en échec.
  useEffect(() => {
    const refresh = () => {
      if (!mounted.current) return;
      setBust(String(Date.now()));
      setFailed(new Set());
    };
    const timer = window.setInterval(refresh, 60_000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const tiles = useMemo(() => (meta ? buildTiles(meta, bust) : []), [meta, bust]);
  const allFailed = tiles.length > 0 && failed.size >= tiles.length;
  const anyLoaded = loadedCount > 0;

  return (
    <div className="relative aspect-square overflow-hidden bg-obsidian-500/60">
      {/* Fond « atlas » steampunk toujours présent (repli gracieux + halo décoratif). */}
      <div
        className="absolute inset-0 opacity-40"
        aria-hidden
        style={{
          backgroundImage:
            "radial-gradient(circle at 30% 40%, rgba(63,182,163,0.35), transparent 22%), radial-gradient(circle at 70% 62%, rgba(139,92,246,0.35), transparent 26%), linear-gradient(rgba(176,118,58,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(176,118,58,0.16) 1px, transparent 1px)",
          backgroundSize: "100% 100%, 100% 100%, 32px 32px, 32px 32px",
        }}
      />

      {/* Grille de tuiles réelles ; masquée tant qu'aucune n'a chargé pour éviter les carrés vides. */}
      {tiles.length > 0 && (
        <div
          className="absolute inset-0 grid transition-opacity duration-500"
          style={{
            gridTemplateColumns: `repeat(${GRID}, 1fr)`,
            gridTemplateRows: `repeat(${GRID}, 1fr)`,
            opacity: anyLoaded ? 1 : 0,
          }}
          aria-hidden
        >
          {tiles.map((t) =>
            failed.has(t.key) ? (
              <div key={t.key} className="bg-obsidian-400/40" />
            ) : (
              <img
                key={t.key}
                src={t.url}
                alt=""
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
                onLoad={() => {
                  if (mounted.current) setLoadedCount((n) => n + 1);
                }}
                onError={() => {
                  if (!mounted.current) return;
                  setFailed((prev) => {
                    if (prev.has(t.key)) return prev;
                    const next = new Set(prev);
                    next.add(t.key);
                    return next;
                  });
                }}
              />
            ),
          )}
        </div>
      )}

      {/* Voile de teinte + vignettage pour l'unité visuelle steampunk. */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          background:
            "radial-gradient(120% 120% at 50% 40%, transparent 55%, rgba(11,10,15,0.55) 100%)",
        }}
      />

      {/* Message discret si la carte est momentanément indisponible (aucune tuile chargée). */}
      {allFailed && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 text-center">
          <span className="text-2xl opacity-40" aria-hidden>
            🧭
          </span>
          <p className="text-xs text-parchment-200/50">Carte momentanément indisponible</p>
        </div>
      )}
    </div>
  );
}
